import { useAuthStore } from '../../stores/auth-store';

/**
 * Decide se o usuário pode acessar (ler/gerir) os Contatos Autorizados de um
 * Bond no tenant atual — Item 350 (honest-defer de UI).
 *
 * Espelha a lógica de READ do `AuthorizedContactsAccessGuard` (engine):
 *
 *   - **permissivo** (`authorizedContactsDpoOnly=false`, default telecom/varejo):
 *     `bond-basic` OU a cap LGPD `lgpd-authorized-contacts-read`.
 *   - **restritivo** (`true`, saúde/jurídico): SÓ a cap LGPD (DPO); `bond-basic`
 *     deixa de bastar.
 *
 * Critério único read+manage: as 2 superfícies do trio (aba Cliente + ficha da
 * Pessoa) só expõem o fluxo "Gerenciar" (abre o CRUD), e a contagem read-only do
 * bloco exige leitura — em restritivo o atendente perde a leitura também (GET
 * 403). Logo, um único helper baseado no critério de READ decide se a superfície
 * inteira aparece. (`-manage` requer `-read`, então quem gere também lê.)
 */
export function canAccessAuthorizedContacts(params: {
  capabilities: string[];
  authorizedContactsDpoOnly: boolean;
}): boolean {
  const hasLgpdRead = params.capabilities.includes('lgpd-authorized-contacts-read');
  if (hasLgpdRead) {
    return true; // DPO — sempre, em qualquer estado do tenant.
  }
  if (params.authorizedContactsDpoOnly) {
    return false; // restritivo: só a cap LGPD basta.
  }
  return params.capabilities.includes('bond-basic'); // permissivo: bond-basic.
}

/**
 * Hook que lê `capabilities[]` + o flag `authorizedContactsDpoOnly` do
 * `useAuthStore` (populados pelo `GET /v1/auth/me` no login) e aplica
 * `canAccessAuthorizedContacts`.
 */
export function useCanAccessAuthorizedContacts(): boolean {
  const capabilities = useAuthStore((s) => s.capabilities);
  const authorizedContactsDpoOnly = useAuthStore(
    (s) => s.tenant?.authorizedContactsDpoOnly ?? false,
  );
  return canAccessAuthorizedContacts({ capabilities, authorizedContactsDpoOnly });
}
